import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { gymManagementService } from '../../../lib/gymManagementService';



const MemberProfileModal = ({ member, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editedMember, setEditedMember] = useState(member);
  const [gyms, setGyms] = useState([]);
  const [loadingGyms, setLoadingGyms] = useState(false);

  useEffect(() => {
    fetchGyms();
  }, []);

  const fetchGyms = async () => {
    setLoadingGyms(true);
    const { data, error } = await gymManagementService?.getAllGyms();
    if (!error && data) {
      setGyms(data);
    }
    setLoadingGyms(false);
  };

  if (!member) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'User' },
    { id: 'membership', label: 'Membership', icon: 'CreditCard' },
    { id: 'billing', label: 'Billing', icon: 'DollarSign' },
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'activity', label: 'Activity', icon: 'Activity' }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const handleSave = () => {
    if (onUpdate) {
      onUpdate(editedMember);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedMember(member);
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditedMember(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmergencyContactChange = (field, value) => {
    setEditedMember(prev => ({
      ...prev,
      emergencyContact: {
        ...prev?.emergencyContact,
        [field]: value
      }
    }));
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      <div className="flex items-start gap-6">
        <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 bg-muted">
          <Image
            src={isEditing ? editedMember?.avatar : member?.avatar}
            alt={isEditing ? editedMember?.avatarAlt : member?.avatarAlt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1">
          {isEditing ? (
            <div className="space-y-3">
              <Input
                label="Name"
                value={editedMember?.name || ''}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
              />
              <Input
                label="Email"
                type="email"
                value={editedMember?.email || ''}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
              />
              <Input
                label="Phone"
                value={editedMember?.phone || ''}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
              />
              <Input
                label="Address"
                value={editedMember?.address || ''}
                onChange={(e) => handleInputChange('address', e?.target?.value)}
              />
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Gym</label>
                <select
                  value={editedMember?.gym_id || ''}
                  onChange={(e) => handleInputChange('gym_id', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground"
                  disabled={loadingGyms}
                >
                  <option value="">Select a gym</option>
                  {gyms?.map((gym) => (
                    <option key={gym?.id} value={gym?.id}>
                      {gym?.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-semibold text-foreground mb-2">{member?.name}</h2>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Mail" size={16} />
                  {member?.email}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Phone" size={16} />
                  {member?.phone}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="MapPin" size={16} />
                  {member?.address}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Building2" size={16} />
                  {member?.gym || 'No gym assigned'}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex flex-col items-center gap-3">
          <div className="bg-white p-3 rounded-lg border-2 border-border">
            <QRCodeSVG
              value={member?.memberId || 'NO-ID'}
              size={120}
              level="H"
              includeMargin={false}
            />
          </div>
          <p className="text-xs text-muted-foreground text-center">Member QR Code</p>
        </div>
      </div>

      {!isEditing && (
        <div className="flex gap-2">
          <Button
            variant="default"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Edit Profile
          </Button>
        </div>
      )}

      {isEditing && (
        <div className="flex gap-2">
          <Button
            variant="default"
            iconName="Save"
            iconPosition="left"
            onClick={handleSave}
          >
            Save Changes
          </Button>
          <Button
            variant="outline"
            iconName="X"
            iconPosition="left"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-muted/50 rounded-lg p-4">
          <p className="text-xs text-muted-foreground mb-1">Date of Birth</p>
          {isEditing ? (
            <Input
              type="date"
              value={editedMember?.dateOfBirth?.split('T')?.[0] || ''}
              onChange={(e) => handleInputChange('dateOfBirth', e?.target?.value)}
            />
          ) : (
            <p className="text-sm font-medium text-foreground">{formatDate(member?.dateOfBirth)}</p>
          )}
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <p className="text-xs text-muted-foreground mb-1">Gender</p>
          {isEditing ? (
            <select
              value={editedMember?.gender || ''}
              onChange={(e) => handleInputChange('gender', e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          ) : (
            <p className="text-sm font-medium text-foreground capitalize">{member?.gender}</p>
          )}
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <p className="text-xs text-muted-foreground mb-1">Member Since</p>
          <p className="text-sm font-medium text-foreground">{formatDate(member?.joinDate)}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <p className="text-xs text-muted-foreground mb-1">Member ID</p>
          <p className="text-sm font-medium text-foreground">{member?.memberId}</p>
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-base font-semibold text-foreground mb-4">Emergency Contact</h3>
        {isEditing ? (
          <div className="space-y-3">
            <Input
              label="Name"
              value={editedMember?.emergencyContact?.name || ''}
              onChange={(e) => handleEmergencyContactChange('name', e?.target?.value)}
            />
            <Input
              label="Relationship"
              value={editedMember?.emergencyContact?.relationship || ''}
              onChange={(e) => handleEmergencyContactChange('relationship', e?.target?.value)}
            />
            <Input
              label="Phone"
              value={editedMember?.emergencyContact?.phone || ''}
              onChange={(e) => handleEmergencyContactChange('phone', e?.target?.value)}
            />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Name:</span>
              <span className="text-sm font-medium text-foreground">{member?.emergencyContact?.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Relationship:</span>
              <span className="text-sm font-medium text-foreground">{member?.emergencyContact?.relationship}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Phone:</span>
              <span className="text-sm font-medium text-foreground">{member?.emergencyContact?.phone}</span>
            </div>
          </div>
        )}
      </div>

      {member?.medicalNotes && (
        <div className="border-t border-border pt-6">
          <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="AlertTriangle" size={18} color="var(--color-warning)" />
            Medical Notes
          </h3>
          {isEditing ? (
            <textarea
              value={editedMember?.medicalNotes || ''}
              onChange={(e) => handleInputChange('medicalNotes', e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground min-h-[100px]"
              placeholder="Enter medical notes..."
            />
          ) : (
            <p className="text-sm text-foreground bg-warning/10 p-4 rounded-lg">{member?.medicalNotes}</p>
          )}
        </div>
      )}
    </div>
  );

  const renderMembershipTab = () => (
    <div className="space-y-6">
      <div className="bg-primary/10 rounded-xl p-6 border border-primary/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Current Membership</h3>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            member?.status === 'active' ? 'bg-success text-success-foreground' : 'bg-error text-error-foreground'
          }`}>
            {member?.status?.charAt(0)?.toUpperCase() + member?.status?.slice(1)}
          </span>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Plan Type:</span>
            <span className="text-sm font-semibold text-foreground">{member?.membershipType}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Start Date:</span>
            <span className="text-sm font-medium text-foreground">{formatDate(member?.joinDate)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Expiry Date:</span>
            <span className="text-sm font-medium text-foreground">{formatDate(member?.expiryDate)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Auto-Renewal:</span>
            <span className="text-sm font-medium text-success">Enabled</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button variant="default" fullWidth iconName="RefreshCw" iconPosition="left">
          Renew Membership
        </Button>
        <Button variant="outline" fullWidth iconName="Pause" iconPosition="left">
          Freeze Account
        </Button>
        <Button variant="destructive" fullWidth iconName="XCircle" iconPosition="left">
          Cancel
        </Button>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-base font-semibold text-foreground mb-4">Membership History</h3>
        <div className="space-y-3">
          {member?.membershipHistory?.map((history, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-foreground">{history?.plan}</p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(history?.startDate)} - {formatDate(history?.endDate)}
                </p>
              </div>
              <span className={`text-xs font-medium ${
                history?.status === 'completed' ? 'text-success' : 'text-muted-foreground'
              }`}>
                {history?.status?.charAt(0)?.toUpperCase() + history?.status?.slice(1)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBillingTab = () => (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border p-6">
        <h3 className="text-base font-semibold text-foreground mb-4">Payment Information</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Payment Method:</span>
            <div className="flex items-center gap-2">
              <Icon name="CreditCard" size={16} />
              <span className="text-sm font-medium text-foreground">•••• 4242</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Next Billing Date:</span>
            <span className="text-sm font-medium text-foreground">{formatDate(member?.nextBillingDate)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Monthly Amount:</span>
            <span className="text-sm font-semibold text-foreground">${member?.monthlyAmount}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button variant="default" fullWidth iconName="CreditCard" iconPosition="left">
          Process Payment
        </Button>
        <Button variant="outline" fullWidth iconName="FileText" iconPosition="left">
          Generate Invoice
        </Button>
        <Button variant="outline" fullWidth iconName="Download" iconPosition="left">
          Export to QuickBooks
        </Button>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-base font-semibold text-foreground mb-4">Payment History</h3>
        <div className="space-y-3">
          {member?.paymentHistory?.map((payment, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon 
                  name={payment?.status === 'paid' ? 'CheckCircle2' : 'XCircle'} 
                  size={20} 
                  color={payment?.status === 'paid' ? 'var(--color-success)' : 'var(--color-error)'}
                />
                <div>
                  <p className="text-sm font-medium text-foreground">${payment?.amount}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(payment?.date)}</p>
                </div>
              </div>
              <span className={`text-xs font-medium ${
                payment?.status === 'paid' ? 'text-success' : 'text-error'
              }`}>
                {payment?.status?.charAt(0)?.toUpperCase() + payment?.status?.slice(1)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDocumentsTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {member?.documents?.map((doc, index) => (
          <div key={index} className="bg-card rounded-xl border border-border p-4 hover:shadow-md transition-all">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="FileText" size={20} color="var(--color-primary)" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-foreground mb-1">{doc?.name}</h4>
                <p className="text-xs text-muted-foreground mb-2">Signed: {formatDate(doc?.signedDate)}</p>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    doc?.status === 'signed' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                  }`}>
                    {doc?.status === 'signed' ? 'Signed' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Button variant="outline" size="sm" fullWidth iconName="Eye" iconPosition="left">
                View
              </Button>
              <Button variant="outline" size="sm" fullWidth iconName="Download" iconPosition="left">
                Download
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Button variant="default" fullWidth iconName="Upload" iconPosition="left">
        Upload New Document
      </Button>
    </div>
  );

  const renderActivityTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-primary/10 rounded-xl p-4 border border-primary/20">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Calendar" size={20} color="var(--color-primary)" />
            <span className="text-sm text-muted-foreground">Total Check-Ins</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{member?.stats?.totalCheckIns}</p>
        </div>
        <div className="bg-success/10 rounded-xl p-4 border border-success/20">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="TrendingUp" size={20} color="var(--color-success)" />
            <span className="text-sm text-muted-foreground">This Month</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{member?.stats?.monthlyCheckIns}</p>
        </div>
        <div className="bg-accent/10 rounded-xl p-4 border border-accent/20">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Award" size={20} color="var(--color-accent)" />
            <span className="text-sm text-muted-foreground">Streak Days</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{member?.stats?.streakDays}</p>
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-base font-semibold text-foreground mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {member?.recentActivity?.map((activity, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={activity?.icon} size={18} color="var(--color-primary)" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{activity?.action}</p>
                <p className="text-xs text-muted-foreground">{formatDate(activity?.timestamp)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Member Profile</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex items-center gap-2 px-6 py-3 border-b border-border overflow-x-auto custom-scrollbar">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span className="text-sm font-medium">{tab?.label}</span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'membership' && renderMembershipTab()}
          {activeTab === 'billing' && renderBillingTab()}
          {activeTab === 'documents' && renderDocumentsTab()}
          {activeTab === 'activity' && renderActivityTab()}
        </div>
      </div>
    </div>
  );
};

export default MemberProfileModal;